import { CheckIcon, CopyIcon } from "@phosphor-icons/react";
import { useCallback, useEffect, useRef, useState } from "react";

/** Lets the customer copy the exact confirmation text with one click. */
export function ConfirmPhraseCopy({ phrase, inputId }: { phrase: string; inputId: string }) {
	const [copied, setCopied] = useState(false);
	const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		return () => {
			if (timer.current) clearTimeout(timer.current);
		};
	}, []);

	const copy = useCallback(async () => {
		try {
			await navigator.clipboard.writeText(phrase);
		} catch {
			return;
		}
		setCopied(true);
		if (timer.current) clearTimeout(timer.current);
		timer.current = setTimeout(() => setCopied(false), 2000);
	}, [phrase]);

	return (
		<span className="inline-flex items-center gap-1">
			<button
				type="button"
				className="inline-flex items-center gap-1 rounded-(--radius-sm) text-dns font-medium text-(--color-brand-600) underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--color-brand-500)"
				onClick={() => void copy()}
				aria-describedby={inputId}
			>
				{phrase}
				{copied ? (
					<CheckIcon className="size-3.5 text-(--color-positive-600)" weight="bold" aria-hidden="true" />
				) : (
					<CopyIcon className="size-3.5" aria-hidden="true" />
				)}
			</button>
			<span role="status" aria-live="polite" className="sr-only">
				{copied ? "Copied to clipboard" : ""}
			</span>
		</span>
	);
}

export function confirmPhrasesMatch(typed: string, phrase: string): boolean {
	const value = typed.trim();
	const expected = phrase.trim();
	if (expected.includes("@")) return value.toLowerCase() === expected.toLowerCase();
	return value === expected;
}
